import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { FileText, Save, AlertCircle } from 'lucide-react';

export default function SettingsDocumentsTab() {
  const { API } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({
    nome: 'Termo de Reserva Padrão',
    conteudo: ''
  });

  const defaultTemplate = `TERMO DE RESERVA DE VEÍCULO

Reserva Nº: {reservation_id}
Data da Reserva: {data_reserva_formatted}

DADOS DO CLIENTE:
Nome: {cliente_nome}
CPF: {cliente_cpf}
Telefone: {cliente_telefone}

DADOS DO VEÍCULO:
{vehicle_info}

VALORES:
Valor Pago: R$ {valor_pago}

PRAZO DE ENTREGA:
Prazo: {prazo_entrega_dias} dias
Previsão de Entrega: {previsao_entrega_formatted}

OBSERVAÇÕES:
{observacoes}

Data de Emissão: {data_emissao}

______________________________
Assinatura do Cliente`;

  useEffect(() => {
    loadTemplate();
  }, []);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/document-templates`, { withCredentials: true });
      const termoReserva = response.data.find(t => t.tipo === 'termo_reserva');
      
      if (termoReserva) {
        setTemplate(termoReserva);
        setFormData({
          nome: termoReserva.nome,
          conteudo: termoReserva.conteudo
        });
      } else {
        // Set default template
        setFormData({
          nome: 'Termo de Reserva Padrão',
          conteudo: defaultTemplate
        });
      }
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Erro ao carregar template');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (template) {
        // Update existing template
        await axios.put(
          `${API}/document-templates/${template.template_id}`,
          {
            nome: formData.nome,
            conteudo: formData.conteudo
          },
          { withCredentials: true }
        );
      } else {
        // Create new template
        await axios.post(
          `${API}/document-templates`,
          {
            tipo: 'termo_reserva',
            nome: formData.nome,
            conteudo: formData.conteudo,
            ativo: true
          },
          { withCredentials: true }
        );
      }
      
      toast.success('Template salvo com sucesso!');
      loadTemplate();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Erro ao salvar template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData({
      nome: 'Termo de Reserva Padrão',
      conteudo: defaultTemplate
    });
  };

  if (loading) {
    return <div className="text-center py-8">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Template do Termo de Reserva
          </CardTitle>
          <CardDescription>
            Configure o template do documento que será gerado ao criar uma reserva.
            Use as variáveis entre chaves para inserir dados dinâmicos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="nome">Nome do Template</Label>
            <Input
              id="nome"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              placeholder="Nome do template"
            />
          </div>

          <div>
            <Label htmlFor="conteudo">Conteúdo do Template</Label>
            <Textarea
              id="conteudo"
              value={formData.conteudo}
              onChange={(e) => setFormData({ ...formData, conteudo: e.target.value })}
              placeholder="Conteúdo do documento..."
              rows={20}
              className="font-mono text-sm"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-blue-900 mb-2">Variáveis Disponíveis:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-blue-800">
                  <div><Badge variant="outline" className="mr-2">{'{reservation_id}'}</Badge>ID da Reserva</div>
                  <div><Badge variant="outline" className="mr-2">{'{data_reserva_formatted}'}</Badge>Data da Reserva</div>
                  <div><Badge variant="outline" className="mr-2">{'{cliente_nome}'}</Badge>Nome do Cliente</div>
                  <div><Badge variant="outline" className="mr-2">{'{cliente_cpf}'}</Badge>CPF do Cliente</div>
                  <div><Badge variant="outline" className="mr-2">{'{cliente_telefone}'}</Badge>Telefone do Cliente</div>
                  <div><Badge variant="outline" className="mr-2">{'{vehicle_info}'}</Badge>Informações do Veículo</div>
                  <div><Badge variant="outline" className="mr-2">{'{valor_pago}'}</Badge>Valor Pago</div>
                  <div><Badge variant="outline" className="mr-2">{'{prazo_entrega_dias}'}</Badge>Prazo em Dias</div>
                  <div><Badge variant="outline" className="mr-2">{'{previsao_entrega_formatted}'}</Badge>Previsão de Entrega</div>
                  <div><Badge variant="outline" className="mr-2">{'{observacoes}'}</Badge>Observações</div>
                  <div><Badge variant="outline" className="mr-2">{'{data_emissao}'}</Badge>Data de Emissão</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : 'Salvar Template'}
            </Button>
            <Button variant="outline" onClick={handleReset}>
              Restaurar Padrão
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
